import { AppIcon } from '@/components/ui/app-icon';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { CARD_LINK } from '@/components/agent-chat/agent-card.component';
import {
  learningRowText,
  parseLearning,
  type LearningDisplay,
} from '@/components/agent-chat/learning-row.util';
import { useCluMemoryEnabled } from '@/hooks/use-clu-memory-flag.hook';
import { iconBrain } from '@/lib/icons';
import { useUndoLearningMutation } from '@/store/features/clu-memory.feature';
import { CONFLICT_WORDS, conflictCode } from '@/store/features/clu-memory.util';

/**
 * A learning as one row, the size of a step: an icon, what happened and the
 * action it offers. A save offers Undo, which settles the row into a word
 * (Undone, or what the server says when it refuses, see CONFLICT_WORDS; any
 * other failure is a toast and the row keeps Undo). A change waiting on the
 * caller offers Review, a link to Settings › Memory where its full text, what
 * it replaces and the quote are shown: the chat never approves or declines
 * one. The display is a snapshot from the turn, so the row reflects the state
 * of that moment until Undo is used here.
 */
export function LearningRow({
  learning,
  text,
}: {
  learning: LearningDisplay;
  text: string;
}) {
  const [undo] = useUndoLearningMutation();
  const [busy, setBusy] = useState(false);
  const [settled, setSettled] = useState<string | null>(null);
  const { learningId, label } = learning;

  const undoLearning = async () => {
    setBusy(true);
    try {
      await undo(learningId).unwrap();
      setSettled('Undone');
    } catch (error) {
      const code = conflictCode(error);
      if (code) setSettled(CONFLICT_WORDS[code]);
      else toast.error('Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-learning-row={learning.state}
      className='flex w-full min-w-0 items-center gap-[4px] text-[12px] leading-[16px] text-muted-foreground'
    >
      <span className='grid size-[20px] shrink-0 place-items-center'>
        <AppIcon icon={iconBrain} style={{ fontSize: 12 }} />
      </span>
      {settled ? (
        <span
          role='status'
          className='min-w-0 truncate font-medium text-foreground/80'
        >
          {settled}
        </span>
      ) : (
        <>
          <span className='min-w-0 truncate font-medium text-foreground/80'>
            {text}
          </span>
          {learning.state === 'saved' && learning.canUndo ? (
            <span className='ml-[8px] flex shrink-0 items-center'>
              <button
                type='button'
                className={CARD_LINK}
                disabled={busy}
                aria-label={`Undo ${label}`}
                onClick={() => void undoLearning()}
              >
                Undo
              </button>
            </span>
          ) : null}
          {learning.state === 'needs_review' && learning.canReview ? (
            <span className='ml-[8px] flex shrink-0 items-center'>
              <Link
                to='/settings/memory'
                search={{ tab: 'review' }}
                className={CARD_LINK}
                aria-label={`Review ${label}`}
              >
                Review
              </Link>
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The part of a message a `learning` display draws: its row, but only while
 * the organization may use what Clu learns. While that is closed or still
 * loading, or the display is not one this reads, nothing.
 */
export function LearningPart({ display }: { display: unknown }) {
  const enabled = useCluMemoryEnabled();
  const learning = enabled ? parseLearning(display) : null;
  const text = learning ? learningRowText(learning) : null;
  if (!learning || !text) return null;
  return (
    <div data-timeline-action>
      <LearningRow learning={learning} text={text} />
    </div>
  );
}
